import { useMemo } from 'react';
import { useAuthStore } from '../stores/authStore';
import { parseToken } from '@billda/permissions-ui';

export interface CurrentUser {
  userId: string;
  email: string;
  name: string;
  initials: string | null;
}

/**
 * Extract initials from user's full name
 * Examples: "John Doe" → "JD", "Jane Smith-Jones" → "JS", "X" → "X"
 * Returns null if name is invalid or empty (triggers Person icon fallback)
 */
const getInitials = (name: string): string | null => {
  if (!name || name.trim() === '') return null;

  const parts = name
    .trim()
    .split(/[\s-]+/)
    .filter(p => p.length > 0);

  if (parts.length === 0) return null;

  if (parts.length === 1) {
    // Single name: take first 2 characters or just 1 if only 1 char
    return parts[0].substring(0, 2).toUpperCase();
  }

  // Multiple parts: take first letter of first two parts
  return parts
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
};

/**
 * Hook to get current authenticated user information from JWT token
 */
export const useCurrentUser = (): CurrentUser | null => {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const token = useAuthStore(state => state.token);

  return useMemo(() => {
    if (!isAuthenticated) return null;
    if (!token) return null;

    const parsed = parseToken(token);
    if (!parsed) return null;

    return {
      userId: parsed.userId,
      email: parsed.email,
      name: parsed.name,
      initials: getInitials(parsed.name),
    };
  }, [isAuthenticated, token]);
};
